import { useEffect, useState, } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import UserLayout from "../../../components/UserLayout";
import FormRenderer from "../forms/renderer/FormRenderer";
import { api } from "../../../utils/api";
import { resolveFormConfig } from "../forms/utils/resolveFormConfig";
import { buildSubmitPayload } from "../forms/utils/buildSubmitPayload";
import { toast } from 'sonner';

export default function ViewApplication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [formConfig, setFormConfig] = useState(null);

  // ✅ ADD: State for uploaded documents
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  const [currentStep, setCurrentStep] = useState("view");
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Dropdowns
  const [nationality, setNationality] = useState([]);
  const [portOfEntry, setPortOfEntry] = useState([]);
  const [portOfExit, setPortOfExit] = useState([]);
  const [previousNationalities, setPreviousNationalities] = useState([]);
  const [otherNationalities, setOtherNationalities] = useState([]);
  const [country, setCountries] = useState([]);

  useEffect(() => {
    fetchApplication();
    fetchDropdowns();
  }, [id]);


  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await api.getApplication(id);

      const appData = response.data?.data || response.data;

      if (!appData) {
        throw new Error("No application data received");
      }

      setApplication(appData);

      const passengerData = appData.passengers?.[0] || {};
      setFormValues(passengerData);

      // ✅ Extract documents from API
      const apiDocuments = appData.documents || [];

      // Load form config first
      await loadFormConfig(appData, apiDocuments);

    } catch (error) {
      console.error("Failed to fetch application:", error);
      setApplication(null);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Modified to handle documents
  const loadFormConfig = async (appData, apiDocuments) => {
    try {
      const category = appData.visa_category;
      const purposeCode = appData.purpose_code;

      const config = await resolveFormConfig(category, purposeCode, id);

      setFormConfig(config);

      // ✅ Combine API documents with any additional info documents
      const allDocuments = [...apiDocuments];

      // Check if additional info section exists and has documents
      const additionalInfoSection = config?.sections?.find(
        s => s.id === 'additional_info_requested'
      );

      if (additionalInfoSection) {
        // If there are additional info documents, fetch them
        // This depends on how your resolveFormConfig handles it
        try {
          const additionalInfoResponse = await api.getAdditionalInfoDocuments(id);
          if (additionalInfoResponse?.data?.documents) {
            allDocuments.push(...additionalInfoResponse.data.documents);
          }
        } catch (error) {
          console.log("No additional info documents found");
        }
      }

      setUploadedDocuments(allDocuments);

    } catch (error) {
      console.error("Failed to load form config:", error);
      setFormConfig(null);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const response = await api.getAlldropdowns();
      const data = response.data?.data || response.data;

      setNationality(data.nationalities || []);
      setPortOfEntry(data.port_of_entry || []);
      setPortOfExit(data.port_of_exit || []);
      setPreviousNationalities(data.previous_nationalities || []);
      setOtherNationalities(data.other_nationalities || []);
      setCountries(data.country_of_birth || []);
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  };

  useEffect(() => {
    if (!application) return;

    const mode = searchParams.get('mode');

    if (mode === 'edit') {
      if (application.status === "draft") {
        setIsEditMode(true);
        setCurrentStep("edit");
      } else {
        navigate(`/applications/${id}`, { replace: true });
        setCurrentStep("view");
      }
    } else {
      setIsEditMode(false);
      setCurrentStep("view");
    }
  }, [searchParams, application, id, navigate]);

  const handleSectionSave = async (values) => {
    if (!id) return;

    try {
      setSaving(true);

      const payload = buildSubmitPayload(values);
      await api.updateApplication(id, payload);

      setFormValues(values);
    } catch (error) {
      console.error("Failed to save section:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveToReview = async (values) => {
    try {
      setSaving(true);

      const payload = buildSubmitPayload(values);
      await api.updateApplication(id, payload);

      setFormValues(values);
      setIsEditMode(false);
      setCurrentStep("review");
    } catch (error) {
      console.error("Failed to save application:", error);
      alert("Failed to save application. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = marketingConsent;

  const handleFinalSubmit = async () => {
    if (!canSubmit) {
      alert("Please accept the declaration to proceed.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...buildSubmitPayload(formValues),
        status: "Application Received, Pending for Review",
        consent_given: true,
      };

      await api.updateApplication(id, payload);

      toast.success("Application submitted successfully!");
      navigate('/applications');
    } catch (error) {
      console.error("Final submission error:", error);
      alert("Failed to submit application.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(`/applications`);
    setCurrentStep("view");
    setIsEditMode(false);
  };

  const handleBack = () => {
    navigate("/applications");
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-sm text-slate-600 font-medium">
              Loading application...
            </p>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!application) {
    return (
      <UserLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              ⚠️
            </div>
            <p className="text-lg text-slate-700 font-medium mb-2">Application not found</p>
            <p className="text-sm text-slate-500 mb-6">
              Application ID: <span className="font-mono">{id}</span>
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Applications
            </button>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!formConfig) {
    return (
      <UserLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              ⚠️
            </div>
            <p className="text-lg text-slate-700 font-medium mb-2">Form configuration not found</p>
            <p className="text-sm text-slate-500 mb-2">
              Visa Category: <span className="font-medium">{application.visa_category || "Unknown"}</span>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Purpose Code: <span className="font-medium">{application.purpose_code || "Unknown"}</span>
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Applications
            </button>
          </div>
        </div>
      </UserLayout>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Application Received, Pending for Review": {
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: "⏳",
      },
      "Additional Information Requested": {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: "📝",
      },
      "Additional Information Received": {
        color: "bg-indigo-50 text-indigo-700 border-indigo-200",
        icon: "✓",
      },
      "Review Passed - Pending for Submission": {
        color: "bg-blue-50 text-blue-700 border-blue-200",
        icon: "📋",
      },
      "Submission Done - Awaiting Visa Status": {
        color: "bg-purple-50 text-purple-700 border-purple-200",
        icon: "⏱️",
      },
      "Visa Approved": {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: "✅",
      },
      "Visa Rejected": {
        color: "bg-red-50 text-red-700 border-red-200",
        icon: "❌",
      },
      draft: {
        color: "bg-slate-50 text-slate-600 border-slate-200",
        icon: "📄",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        <span>{config.icon}</span>
        {status}
      </span>
    );
  };

  return (
    <UserLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* HEADER */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Applications
          </button>

          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-4">

            {/* LEFT SECTION */}
            <div className="space-y-2">

              {/* Title */}
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
                {currentStep === "edit"
                  ? "Edit Application"
                  : currentStep === "review"
                    ? "Review Application"
                    : currentStep === "submit"
                      ? "Submit Application"
                      : ""}
              </h1>

              {/* Meta Information Row */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-600">

                {/* Application ID */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Application ID:</span>
                  <span className="font-mono font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                    {id}
                  </span>
                </div>

                {/* Visa Category */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Visa Category:</span>
                  <span className="font-medium text-slate-800">
                    {application.visa_category || "N/A"}
                  </span>
                </div>

                {/* Visa Type */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Visa Type:</span>
                  <span className="font-medium text-slate-800">
                    {application.visa_type_name || "N/A"}
                  </span>
                </div>

                {/* Status Badge */}
                <div className="ml-1">
                  {getStatusBadge(application.status)}
                </div>

              </div>

            </div>

            {/* RIGHT SECTION (Actions) */}
            <div className="flex items-center gap-3">

              {currentStep === "edit" && (
                <button
                  onClick={handleCancelEdit}
                  className="
          px-5 py-2
          text-sm font-medium
          text-slate-700
          bg-white
          border border-slate-300
          rounded-lg
          hover:bg-slate-50
          hover:border-slate-400
          transition-all duration-200
          shadow-sm
        "
                >
                  Cancel
                </button>
              )}

            </div>

          </div>

        </div>

        {/* ✅ STEP 1: EDIT MODE */}
        {currentStep === "edit" && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <FormRenderer
              config={formConfig}
              initialValues={formValues}
              onSubmit={handleMoveToReview}
              onSectionSave={handleSectionSave}
              nationality={nationality}
              portOfEntry={portOfEntry}
              portOfExit={portOfExit}
              previousNationalities={previousNationalities}
              otherNationalities={otherNationalities}
              country={country}
              mode="edit"
              allowNavigation={false}
              uploadedDocuments={uploadedDocuments} // ✅ Pass actual documents
              applicationId={id}
            />
          </div>
        )}

        {/* ✅ STEP 2: REVIEW MODE */}
        {currentStep === "review" && (
          <>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <FormRenderer
                config={formConfig}
                initialValues={formValues}
                onSubmit={() => { }}
                nationality={nationality}
                portOfEntry={portOfEntry}
                portOfExit={portOfExit}
                previousNationalities={previousNationalities}
                otherNationalities={otherNationalities}
                country={country}
                mode="review"
                allowNavigation={true}
                uploadedDocuments={uploadedDocuments} // ✅ Pass actual documents
                applicationId={id}
              />
            </div>

            <div className="flex justify-end pt-6">
              <button
                type="button"
                onClick={() => setCurrentStep("submit")}
                disabled={saving}
                className="px-8 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 shadow-lg disabled:bg-gray-300"
              >
                {saving ? "Processing..." : "Proceed to Submit"}
              </button>
            </div>
          </>
        )}

        {/* ✅ STEP 3: SUBMIT MODE */}
        {currentStep === "submit" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Review & Submit Application
            </h2>

            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {application.applicant_name || "N/A"}
              </h3>

              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <span className="font-medium">Application ID:</span>{" "}
                  <span className="text-gray-900 font-mono">{id}</span>
                </p>

                <p className="text-gray-700">
                  <span className="font-medium">Passport:</span>{" "}
                  <span className="text-gray-900">
                    {formValues.passport_number || "Not provided"}
                  </span>
                </p>

                {/* <p className="text-gray-700">
                  <span className="font-medium">Visa Type:</span>{" "}
                  <span className="text-gray-900">
                    {application.visa_type_name || "N/A"}
                  </span>
                </p> */}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-800">
                  <p>
                    I hereby declare that all information provided for all passengers is true and correct to the best of my knowledge.
                  </p>
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => setCurrentStep("review")}
                className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                Back to Review
              </button>

              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={!canSubmit || saving}
                className={`px-8 py-3 rounded-xl font-semibold transition-all ${canSubmit && !saving
                    ? "bg-green-600 text-white hover:bg-green-700 shadow-lg"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
              >
                {saving ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </div>
        )}

        {/* ✅ STEP 4: VIEW MODE */}
        {currentStep === "view" && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <FormRenderer
              config={formConfig}
              initialValues={formValues}
              onSubmit={() => { }}
              nationality={nationality}
              portOfEntry={portOfEntry}
              portOfExit={portOfExit}
              previousNationalities={previousNationalities}
              otherNationalities={otherNationalities}
              country={country}
              mode="view"
              allowNavigation={true}
              uploadedDocuments={uploadedDocuments} // ✅ Pass actual documents
              applicationId={id}
            />
          </div>
        )}

        {saving && (
          <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
            Saving changes...
          </div>
        )}
      </div>
    </UserLayout>
  );
}